import { useMemo } from "react"
import { useTranslation } from "react-i18next"

import {
  Action,
  ActionGroup,
  ActionGroupTypename,
  isActionGroup
} from "shared/types/Action"
import { IconName } from "shared/ui/icon/__generated__/IconName"

import { useProjectActions } from "features/project-management/model/useProjectActions"

import { ProjectActionsOverlayProps } from "../ProjectActionsOverlay"
import { ProjectActionsOverlayViewModel } from "../ProjectActionsOverlayView"

function makeGroup(
  id: string,
  icon: IconName,
  label: string,
  actions: Action[]
): ActionGroup {
  return {
    id: `project__actions-overlay__group--${id}`,
    key: `group--${id}`,
    icon,
    label,
    actions,
    __typename: ActionGroupTypename
  }
}

export function useProjectActionsOverlayViewModel({
  isShared,
  additionalActions,
  onCancel,
  ...callbacks
}: ProjectActionsOverlayProps): ProjectActionsOverlayViewModel {
  const { t } = useTranslation()

  const {
    editAction,
    newProductAction,
    newRoomAction,
    newFloorAction,
    exportAction,
    exportGaebAction,
    musterheftExportAction,
    attachmentsAction,
    shareAction,
    addClarificationRequestAction,
    inviteTeamMemberAction,
    manageTeamAction,
    duplicateAction,
    activateAction,
    deleteAction
  } = useProjectActions({
    idPrefix: "project__actions-overlay",
    isShared,
    ...callbacks,
    onOpen: undefined,
    additionalActions
  })

  const actions = useMemo((): (Action | ActionGroup)[] => {
    const addGroup = makeGroup(
      "add",
      IconName.PLUS_CIRCLE,
      t("Project.ActionGroups.Add"),
      [
        { ...newProductAction, label: t("Product.Product") },
        { ...newRoomAction, label: t("Room.Room") },
        { ...newFloorAction, label: t("Floor.Floor") }
      ]
    )
    const exportGroup = makeGroup(
      "export",
      IconName.EXPORT,
      t("Project.ActionGroups.Export"),
      [exportAction, exportGaebAction, musterheftExportAction]
    )
    const collaborationGroup = makeGroup(
      "collaboration",
      IconName.TEAM,
      t("Project.ActionGroups.Collaboration"),
      [
        inviteTeamMemberAction,
        shareAction,
        addClarificationRequestAction,
        manageTeamAction
      ]
    )
    const moreOptionsGroup = makeGroup(
      "more-options",
      IconName.MORE_OPTIONS,
      t("Project.ActionGroups.MoreOptions"),
      [duplicateAction, activateAction]
    )

    return [
      editAction,
      addGroup,
      exportGroup,
      collaborationGroup,
      attachmentsAction,
      moreOptionsGroup,
      ...(additionalActions ?? []),
      deleteAction
    ].filter(a => isActionGroup(a) || a.onClick !== undefined)
  }, [
    t,
    editAction,
    newProductAction,
    newRoomAction,
    newFloorAction,
    exportAction,
    exportGaebAction,
    musterheftExportAction,
    inviteTeamMemberAction,
    shareAction,
    addClarificationRequestAction,
    manageTeamAction,
    attachmentsAction,
    duplicateAction,
    activateAction,
    additionalActions,
    deleteAction
  ])

  return { actions, onCancel }
}
